import type { FilterFn, Row } from '@tanstack/react-table';
import { isNil } from 'es-toolkit';
import { DateTime, Interval } from 'luxon';

import type {
  CheckboxFilterValue,
  DateFilterValue,
  DateTimeFilterValue,
  NumberFilterValue,
  SelectFilterValue,
  TextFilterValue,
  TimeFilterValue,
} from '../types/filter-values';

/**
 * Filter function for select/tag filters.
 * Handles anyOf, noneOf, and isEmpty operators.
 */
export function selectFilterFn(row: Row<unknown>, columnId: string, filterValue: SelectFilterValue): boolean {
  const cellValue = row.getValue(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue) || cellValue === '';
  }

  // Handle array cell values (e.g., tags)
  if (Array.isArray(cellValue)) {
    if (filterValue.operator === 'anyOf') {
      return filterValue.values.some((value) => cellValue.includes(value));
    }
    return !filterValue.values.some((value) => cellValue.includes(value));
  }

  // Handle scalar cell values
  if (filterValue.operator === 'anyOf') {
    return filterValue.values.includes(cellValue);
  }

  return !filterValue.values.includes(cellValue);
}

/**
 * Filter function for text filters.
 * Handles contains, equals, startsWith, endsWith, and isEmpty operators.
 */
export function textFilterFn(row: Row<unknown>, columnId: string, filterValue: TextFilterValue): boolean {
  const cellValue = row.getValue<string | undefined | null>(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue) || cellValue === '';
  }

  const cellString = String(cellValue ?? '').toLowerCase();
  const filterString = filterValue.value.toLowerCase();

  switch (filterValue.operator) {
    case 'contains':
      return cellString.includes(filterString);
    case 'equals':
      return cellString === filterString;
    case 'startsWith':
      return cellString.startsWith(filterString);
    case 'endsWith':
      return cellString.endsWith(filterString);
    default:
      return true;
  }
}

/**
 * Filter function for number/currency filters.
 * Handles eq, gt, gte, lt, lte, between, and isEmpty operators.
 */
export function numberFilterFn(row: Row<unknown>, columnId: string, filterValue: NumberFilterValue): boolean {
  const cellValue = row.getValue<number | undefined | null>(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue);
  }

  const numValue = Number(cellValue);

  if (isNaN(numValue)) {
    return false;
  }

  switch (filterValue.operator) {
    case 'eq':
      return numValue === filterValue.value;
    case 'gt':
      return numValue > filterValue.value;
    case 'gte':
      return numValue >= filterValue.value;
    case 'lt':
      return numValue < filterValue.value;
    case 'lte':
      return numValue <= filterValue.value;
    case 'between':
      return numValue >= filterValue.min && numValue <= filterValue.max;
    default:
      return true;
  }
}

/**
 * Filter function for date filters.
 * Handles eq, before, after, between, and isEmpty operators.
 */
export function dateFilterFn(row: Row<unknown>, columnId: string, filterValue: DateFilterValue): boolean {
  const cellValue = row.getValue<string | undefined | null>(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue) || cellValue === '';
  }

  const cellDate = DateTime.fromISO(cellValue ?? '');

  if (!cellDate.isValid) {
    return false;
  }

  switch (filterValue.operator) {
    case 'eq': {
      const filterDate = DateTime.fromISO(filterValue.value);
      return cellDate.hasSame(filterDate, 'day');
    }
    case 'before': {
      const filterDate = DateTime.fromISO(filterValue.value).startOf('day');
      return cellDate.startOf('day') < filterDate;
    }
    case 'after': {
      const filterDate = DateTime.fromISO(filterValue.value).startOf('day');
      return cellDate.startOf('day') > filterDate;
    }
    case 'between': {
      const startDate = DateTime.fromISO(filterValue.start).startOf('day');
      const endDate = DateTime.fromISO(filterValue.end).endOf('day');
      const interval = Interval.fromDateTimes(startDate, endDate);
      return interval.contains(cellDate);
    }
    default:
      return true;
  }
}

/**
 * Filter function for time filters.
 * Handles eq, before, after, between, and isEmpty operators.
 */
export function timeFilterFn(row: Row<unknown>, columnId: string, filterValue: TimeFilterValue): boolean {
  const cellValue = row.getValue<string | undefined | null>(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue) || cellValue === '';
  }

  const cellTime = DateTime.fromISO(cellValue ?? '');

  if (!cellTime.isValid) {
    return false;
  }

  switch (filterValue.operator) {
    case 'eq': {
      const filterTime = DateTime.fromISO(filterValue.value);
      return cellTime.equals(filterTime);
    }
    case 'before': {
      const filterTime = DateTime.fromISO(filterValue.value);
      return cellTime < filterTime;
    }
    case 'after': {
      const filterTime = DateTime.fromISO(filterValue.value);
      return cellTime > filterTime;
    }
    case 'between': {
      const startTime = DateTime.fromISO(filterValue.start);
      const endTime = DateTime.fromISO(filterValue.end);
      const interval = Interval.fromDateTimes(startTime, endTime);
      return interval.contains(cellTime);
    }
    default:
      return true;
  }
}

/**
 * Filter function for datetime filters.
 * Handles eq, before, after, between, and isEmpty operators.
 */
export function datetimeFilterFn(row: Row<unknown>, columnId: string, filterValue: DateTimeFilterValue): boolean {
  const cellValue = row.getValue<string | undefined | null>(columnId);

  if (filterValue.operator === 'isEmpty') {
    return isNil(cellValue) || cellValue === '';
  }

  const cellDate = DateTime.fromISO(cellValue ?? '');

  if (!cellDate.isValid) {
    return false;
  }

  switch (filterValue.operator) {
    case 'eq': {
      const filterDateTime = DateTime.fromISO(filterValue.value);
      return cellDate.equals(filterDateTime);
    }
    case 'before': {
      const filterDateTime = DateTime.fromISO(filterValue.value);
      return cellDate < filterDateTime;
    }
    case 'after': {
      const filterDateTime = DateTime.fromISO(filterValue.value);
      return cellDate > filterDateTime;
    }
    case 'between': {
      const startDateTime = DateTime.fromISO(filterValue.start);
      const endDateTime = DateTime.fromISO(filterValue.end);
      const interval = Interval.fromDateTimes(startDateTime, endDateTime);
      return interval.contains(cellDate);
    }
    default:
      return true;
  }
}

/**
 * Filter function for checkbox filters.
 * Handles is and isNot operators.
 */
export function checkboxFilterFn(row: Row<unknown>, columnId: string, filterValue: CheckboxFilterValue): boolean {
  const cellValue = row.getValue<boolean | undefined | null>(columnId);
  const boolValue = Boolean(cellValue);

  if (filterValue.operator === 'is') {
    return boolValue === filterValue.value;
  }

  return boolValue !== filterValue.value;
}

/**
 * Map of filter type to filter function.
 */
export const FILTER_FN_BY_TYPE: Record<string, FilterFn<unknown>> = {
  select: selectFilterFn,
  tag: selectFilterFn,
  text: textFilterFn,
  number: numberFilterFn,
  currency: numberFilterFn,
  date: dateFilterFn,
  time: timeFilterFn,
  datetime: datetimeFilterFn,
  checkbox: checkboxFilterFn,
};

/**
 * Gets the appropriate filter function for a filter type.
 */
export function getFilterFnForType(type: string): FilterFn<unknown> | undefined {
  return FILTER_FN_BY_TYPE[type];
}
